// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8081/api";
let unauthorizedEventSent = false;

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const token = localStorage.getItem("tarewu_token");
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = text;
      }

      if (!response.ok) {
        if (response.status === 401 && endpoint !== "/auth/login") {
          localStorage.removeItem("tarewu_token");
          localStorage.removeItem("tarewu_user");
          if (!unauthorizedEventSent) {
            unauthorizedEventSent = true;
            window.dispatchEvent(
              new CustomEvent("tarewu:unauthorized", {
                detail: {
                  message: "Your session has expired. Please sign in again.",
                },
              }),
            );
          }
        }
        // CodeIgniter's fail*() helpers put the text under messages.error (or one key per field).
        const ciMessages = data && typeof data.messages === "object" && data.messages ? Object.values(data.messages).filter(Boolean) : [];
        const message =
          data && data.message
            ? data.message
            : ciMessages.length
              ? ciMessages.join("\n")
              : typeof data === "string"
              ? data
              : JSON.stringify(data);
        const error = new Error(
          response.status === 401 && endpoint !== "/auth/login"
            ? "Your session has expired. Please sign in again."
            : message || `API request failed (${response.status})`,
        );
        error.status = response.status;
        error.isUnauthorized =
          response.status === 401 && endpoint !== "/auth/login";
        throw error;
      }

      if (endpoint === "/auth/login") unauthorizedEventSent = false;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async publicRequest(endpoint, options = {}) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.messages?.error || data.message || `Request failed (${response.status})`);
    return data;
  }

  async openAuthenticatedHtml(endpoint) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Could not generate printable document.");
    }
    const html = await response.text();
    const win = window.open("", "_blank");
    if (!win) throw new Error("Allow pop-ups to open the printable document.");
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  // Members
  async getMembers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/members${queryString ? `?${queryString}` : ""}`);
  }

  async getMember(id) {
    return this.request(`/members/${id}`);
  }

  async getMyMember() {
    return this.request("/members/me");
  }

  // Auth
  async login(email, password) {
    return this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyElectionLoginOTP(challenge, otp) {
    return this.request("/auth/election-otp/verify", {
      method: "POST",
      body: JSON.stringify({ challenge, otp }),
    });
  }

  async logout() {
    return this.request("/auth/logout", {
      method: "POST",
    });
  }

  async me() {
    return this.request("/auth/me");
  }

  async getCommunicationSettings() {
    return this.request("/settings/communications");
  }
  async getEngagementSurveys(){return this.request("/engagement/surveys");}
  async getEngagementSurvey(id){return this.request(`/engagement/surveys/${id}`);}
  async createEngagementSurvey(data){return this.request("/engagement/surveys",{method:"POST",body:JSON.stringify(data)});}
  async transitionEngagementSurvey(id,action){return this.request(`/engagement/surveys/${id}/transition/${action}`,{method:"POST"});}
  async submitEngagementResponse(id,answers){return this.request(`/engagement/surveys/${id}/responses`,{method:"POST",body:JSON.stringify({answers})});}
  async getMyFeedback(){return this.request("/engagement/feedback");}
  async submitFeedback(data){return this.request("/engagement/feedback",{method:"POST",body:JSON.stringify(data)});}
  async handleFeedback(id,data){return this.request(`/engagement/feedback/${id}`,{method:"PUT",body:JSON.stringify(data)});}
  async getEngagementReport(){return this.request("/engagement/report");}
  async getVideoIntegrationSettings() {
    return this.request("/settings/video-integrations");
  }
  async saveVideoIntegrationSettings(data) {
    return this.request("/settings/video-integrations", { method: "PUT", body: JSON.stringify(data) });
  }
  async testVideoIntegration(provider) {
    return this.request(`/settings/video-integrations/${provider}/test`, { method: "POST" });
  }

  async getTrainings() {
    return this.request("/trainings");
  }
  async createTraining(data) {
    return this.request("/trainings", { method: "POST", body: JSON.stringify(data) });
  }
  async updateTraining(id, data) {
    return this.request(`/trainings/${id}`, { method: "PUT", body: JSON.stringify(data) });
  }
  async registerTraining(id) {
    return this.request(`/trainings/${id}/register`, { method: "POST" });
  }
  async payTraining(id) { return this.request(`/trainings/${id}/payment`, { method: "POST" }); }
  async joinTraining(id) {
    return this.request(`/trainings/${id}/join`, { method: "POST" });
  }
  async getTrainingRegistrations(id) {
    return this.request(`/trainings/${id}/registrations`);
  }
  async updateTrainingRegistration(trainingId, registrationId, data) {
    return this.request(`/trainings/${trainingId}/registrations/${registrationId}`, { method: "PUT", body: JSON.stringify(data) });
  }
  async getBrandingSettings() {
    return this.request("/settings/branding");
  }
  async getGeneralSettings() {
    return this.request("/settings/general");
  }
  async getModuleVisibility() { return this.request("/settings/modules"); }
  async saveModuleVisibility(data) { return this.request("/settings/modules", { method: "PUT", body: JSON.stringify(data) }); }
  async getAiSettings(){return this.request("/settings/ai");}
  async saveAiSettings(data){return this.request("/settings/ai",{method:"PUT",body:JSON.stringify(data)});}
  async getPaymentGatewaySettings(){return this.request("/settings/payment-gateways");}
  async savePaymentGatewaySettings(data){return this.request("/settings/payment-gateways",{method:"PUT",body:JSON.stringify(data)});}
  async getAiStatus(){return this.request("/ai/status");}
  async getAiConversations(){return this.request("/ai/conversations");}
  async getAiConversation(id){return this.request(`/ai/conversations/${id}`);}
  async askAi(data){return this.request("/ai/ask",{method:"POST",body:JSON.stringify(data)});}
  async rateAiMessage(id,feedback){return this.request(`/ai/messages/${id}/feedback`,{method:"PUT",body:JSON.stringify({feedback})});}
  async indexDocumentForAi(id){return this.request(`/documents/${id}/ai-index`,{method:"POST"});}
  async getCases(params={}){const q=new URLSearchParams(Object.entries(params).filter(([,v])=>v!==""&&v!=null)).toString();return this.request(`/cases${q?`?${q}`:""}`);}
  async getCase(id){return this.request(`/cases/${id}`);}
  async createCase(data){return this.request("/cases",{method:"POST",body:JSON.stringify(data)});}
  async updateCase(id,data){return this.request(`/cases/${id}`,{method:"PUT",body:JSON.stringify(data)});}
  async withdrawCase(id,reason){return this.request(`/cases/${id}`,{method:"DELETE",body:JSON.stringify({reason})});}
  async getCaseMetadata(){return this.request("/cases/metadata");}
  async assignCase(id,assigned_to){return this.request(`/cases/${id}/assign`,{method:"POST",body:JSON.stringify({assigned_to})});}
  async transitionCase(id,data){return this.request(`/cases/${id}/status`,{method:"POST",body:JSON.stringify(data)});}
  async addCaseEvent(id,data){return this.request(`/cases/${id}/events`,{method:"POST",body:JSON.stringify(data)});}
  async uploadCaseDocument(id,data){const token=localStorage.getItem("tarewu_token"),form=new FormData();Object.entries(data).forEach(([k,v])=>v!=null&&form.append(k,v));const response=await fetch(`${this.baseUrl}/cases/${id}/documents`,{method:"POST",headers:token?{Authorization:`Bearer ${token}`}:{},body:form});const result=await response.json();if(!response.ok)throw new Error(result.message||"Document upload failed.");return result;}
  async downloadCaseDocument(caseId,documentId,name){const token=localStorage.getItem("tarewu_token"),response=await fetch(`${this.baseUrl}/cases/${caseId}/documents/${documentId}/download`,{headers:token?{Authorization:`Bearer ${token}`}:{}});if(!response.ok)throw new Error("Document download failed.");const blob=await response.blob(),url=URL.createObjectURL(blob),a=document.createElement("a");a.href=url;a.download=name;a.click();URL.revokeObjectURL(url);}
  async getMeetingGovernanceSettings() {
    return this.request("/settings/meeting-governance");
  }
  async saveMeetingGovernanceSettings(data) {
    return this.request("/settings/meeting-governance", { method: "PUT", body: JSON.stringify(data) });
  }
  async getElectionSettings() {
    return this.request("/settings/elections");
  }
  async saveElectionSettings(data) {
    return this.request("/settings/elections", { method: "PUT", body: JSON.stringify(data) });
  }
  async saveGeneralSettings(data) {
    return this.request("/settings/general", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async uploadSystemLogo(file) {
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("logo", file);
    const response = await fetch(`${this.baseUrl}/settings/logo`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Logo upload failed.");
    return data;
  }
  async testEmailSettings(recipient) {
    return this.request("/settings/email/test", {
      method: "POST",
      body: JSON.stringify({ recipient }),
    });
  }
  async saveSmsSettings(data) {
    return this.request("/settings/sms", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async testSmsSettings(phone) {
    return this.request("/settings/sms/test", {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
  }

  async getStaffProfile() {
    return this.request("/staff-profile");
  }
  async getDocuments(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/documents${query ? `?${query}` : ""}`);
  }
  async getDocumentCategories() { return this.request("/documents/categories"); }
  async uploadDocument(data) {
    const token = localStorage.getItem("tarewu_token"); const form = new FormData();
    Object.entries(data).forEach(([key,value]) => value !== null && value !== undefined && form.append(key,value));
    const response = await fetch(`${this.baseUrl}/documents`,{method:"POST",headers:token?{Authorization:`Bearer ${token}`}:{},body:form});const result=await response.json();if(!response.ok)throw new Error(result.message||"Document upload failed.");return result;
  }
  async transitionDocument(id, action) { return this.request(`/documents/${id}/transition/${action}`,{method:"POST"}); }
  async downloadDocument(id, name="document") { const token=localStorage.getItem("tarewu_token");const response=await fetch(`${this.baseUrl}/documents/${id}/download`,{headers:token?{Authorization:`Bearer ${token}`}:{}});if(!response.ok)throw new Error("Document download failed.");const blob=await response.blob();const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=name;a.click();URL.revokeObjectURL(url); }
  async updateStaffProfile(data) {
    return this.request("/staff-profile", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async createMember(data) {
    return this.request("/members", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMember(id, data) {
    return this.request(`/members/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteMember(id) {
    return this.request(`/members/${id}`, {
      method: "DELETE",
    });
  }

  async uploadMemberPhoto(memberId, file) {
    const token = localStorage.getItem("tarewu_token");
    const formData = new FormData();
    formData.append("photo", file);

    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/members/${memberId}/photo`, {
      method: "POST",
      headers,
      body: formData,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      data = text;
    }

    if (!response.ok) {
      const message =
        data && data.message ? data.message : "Photo upload failed";
      throw new Error(message);
    }

    return data;
  }

  async getMemberDashboardStats() {
    return this.request("/members/dashboard/stats");
  }

  async getMemberProfile() {
    return this.request("/members/me");
  }

  async updateMemberProfile(data) {
    return this.request("/members/me", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getBranchOptions() {
    return this.request("/branches/options");
  }

  async uploadProfilePicture(file) {
    const token = localStorage.getItem("tarewu_token");
    const formData = new FormData();
    formData.append("photo", file);

    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/members/me/photo`, {
      method: "POST",
      headers,
      body: formData,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      data = text;
    }

    if (!response.ok) {
      const message =
        data && data.message
          ? data.message
          : typeof data === "string"
            ? data
            : JSON.stringify(data);
      throw new Error(message || `API request failed (${response.status})`);
    }

    return data;
  }

  // Contributions
  async getContributions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(
      `/contributions${queryString ? `?${queryString}` : ""}`,
    );
  }

  async createContribution(data) {
    return this.request("/contributions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async prepareContributionAccounting(id, data) {
    return this.request(`/contributions/${id}/prepare-accounting`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getMemberContributions(memberId) {
    return this.request(`/contributions/member/${memberId}`);
  }

  async importContributions(file) {
    const token = localStorage.getItem("tarewu_token");
    const formData = new FormData();
    formData.append("file", file);

    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/contributions/import`, {
      method: "POST",
      headers,
      body: formData,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      data = text;
    }

    if (!response.ok) {
      const message =
        data && data.message
          ? data.message
          : typeof data === "string"
            ? data
            : JSON.stringify(data);
      throw new Error(message || `API request failed (${response.status})`);
    }

    return data;
  }

  async inviteMember(memberId, data) {
    return this.request(`/members/${memberId}/invite`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getMemberDocuments(memberId) {
    return this.request(`/members/${memberId}/documents`);
  }

  async uploadMemberDocument(memberId, file, documentType, title) {
    const token = localStorage.getItem("tarewu_token");
    const formData = new FormData();
    formData.append("document", file);
    formData.append("document_type", documentType);
    formData.append("title", title);
    const response = await fetch(
      `${this.baseUrl}/members/${memberId}/documents`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      },
    );
    const data = await response.json();
    if (!response.ok)
      throw new Error(data?.message || "Document upload failed");
    return data;
  }

  async activateAccount(token, password) {
    return this.request("/auth/activate", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  }

  async resendActivation(email) {
    return this.request("/auth/resend", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async getMonthlyContributionsSummary(year) {
    return this.request(`/contributions/monthly-summary/${year}`);
  }

  async processPayrollDeduction(month, year, deductionRate = 4) {
    return this.request("/contributions/process-payroll", {
      method: "POST",
      body: JSON.stringify({ month, year, deduction_rate: deductionRate }),
    });
  }

  async uploadPayroll(file, month, year, deductionRate = 4) {
    const url = `${this.baseUrl}/payroll/upload`;
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("file", file);
    form.append("month", month);
    form.append("year", year);
    form.append("deduction_rate", deductionRate);

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: form,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Payroll upload failed");
    return data;
  }

  async uploadPayrollRows(rows, month, year, deductionRate = 4) {
    return this.request("/payroll/upload", {
      method: "POST",
      body: JSON.stringify({
        rows,
        month,
        year,
        deduction_rate: deductionRate,
      }),
    });
  }

  // Loans
  async getLoans(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/loans${queryString ? `?${queryString}` : ""}`);
  }

  async getLoan(id) {
    return this.request(`/loans/${id}`);
  }

  async createLoan(data) {
    return this.request("/loans", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async approveLoan(id, data = {}) {
    return this.request(`/loans/approve/${id}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async rejectLoan(id, data = {}) {
    return this.request(`/loans/reject/${id}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async disburseLoan(id, data = {}) {
    return this.request(`/loans/disburse/${id}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getLoanRepaymentSchedule(loanId) {
    return this.request(`/loans/repayment-schedule/${loanId}`);
  }

  async getPendingLoans() {
    return this.request("/loans/pending");
  }

  async getLoansSummary() {
    return this.request("/loans/summary");
  }

  // Communications
  async getCommunications(params = {}, options = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/communications${queryString ? `?${queryString}` : ""}`, options);
  }

  // Users
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/users${queryString ? `?${queryString}` : ""}`);
  }

  async createUser(data) {
    return this.request("/users", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getRoles() {
    return this.request("/roles");
  }

  async createRole(data) {
    return this.request("/roles", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getUserPermissions(id) {
    return this.request(`/users/${id}/permissions`);
  }

  async updateUserPermissions(id, overrides) {
    return this.request(`/users/${id}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ overrides }),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: "DELETE",
    });
  }

  // Branches
  async getBranches(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/branches${queryString ? `?${queryString}` : ""}`);
  }

  async getBranch(id) {
    return this.request(`/branches/${id}`);
  }

  async createBranch(data) {
    return this.request("/branches", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async importBranches(file) {
    const url = `${this.baseUrl}/branches/import`;
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("file", file);

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: form,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Branches import failed");
    return data;
  }

  async updateBranch(id, data) {
    return this.request(`/branches/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteBranch(id) {
    return this.request(`/branches/${id}`, {
      method: "DELETE",
    });
  }

  async getCommunication(id) {
    return this.request(`/communications/${id}`);
  }

  async createCommunication(data) {
    console.log("createCommunication - data to send:", data);
    return this.request("/communications", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  async sendCommunication(id) {
    return this.request(`/communications/send/${id}`, {
      method: "POST",
    });
  }

  async scheduleCommunication(id, scheduledAt) {
    return this.request(`/communications/schedule/${id}`, {
      method: "POST",
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    });
  }

  async getRecentAnnouncements() {
    return this.request("/communications/recent");
  }

  async getCommunicationsStats() {
    return this.request("/communications/stats");
  }

  // Meetings
  async getMeetings(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/meetings${queryString ? `?${queryString}` : ""}`);
  }

  async getMeeting(id) {
    return this.request(`/meetings/${id}`);
  }

  async createMeeting(data) {
    return this.request("/meetings", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMeeting(id, data) {
    return this.request(`/meetings/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteMeeting(id) {
    return this.request(`/meetings/${id}`, {
      method: "DELETE",
    });
  }

  async addMeetingProposal(meetingId, data) {
    return this.request(`/meetings/${meetingId}/proposals`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionMeeting(id, action) {
    return this.request(`/meetings/${id}/transition/${action}`, { method: "POST" });
  }
  async updateMeetingAttendance(meetingId, participantId, attendance_status) {
    return this.request(`/meetings/${meetingId}/participants/${participantId}`, { method: "PUT", body: JSON.stringify({ attendance_status }) });
  }
  async saveMeetingMinutes(id, minutes) {
    return this.request(`/meetings/${id}/minutes`, { method: "PUT", body: JSON.stringify({ minutes }) });
  }
  async addMeetingResolution(id, data) {
    return this.request(`/meetings/${id}/resolutions`, { method: "POST", body: JSON.stringify(data) });
  }
  async addMeetingAction(id, data) {
    return this.request(`/meetings/${id}/actions`, { method: "POST", body: JSON.stringify(data) });
  }
  async updateMeetingAction(meetingId, actionId, data) {
    return this.request(`/meetings/${meetingId}/actions/${actionId}`, { method: "PUT", body: JSON.stringify(data) });
  }
  async createMeetingAllowanceRequisition(meetingId, data) {
    return this.request(`/meetings/${meetingId}/allowance-requisition`, { method: "POST", body: JSON.stringify(data) });
  }

  async getMeetingMinutes(meetingId) {
    return this.request(`/meetings/${meetingId}/minutes`);
  }

  // Elections
  async getElections(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/elections${queryString ? `?${queryString}` : ""}`);
  }

  async getElection(id) {
    return this.request(`/elections/${id}`);
  }

  async createElection(data) {
    return this.request("/elections", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateElection(id, data) {
    return this.request(`/elections/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async transitionElection(id, action) {
    return this.request(`/elections/${id}/transition/${action}`, {
      method: "POST",
      body: "{}",
    });
  }
  async saveElectionCommittee(id, data) {
    return this.request(`/elections/${id}/committee`, { method: "PUT", body: JSON.stringify(data) });
  }
  async decideElectionCandidate(
    electionId,
    candidateId,
    action,
    reason = null,
  ) {
    return this.request(
      `/elections/${electionId}/candidates/${candidateId}/${action}`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  }
  async getElectionMetadata(id) {
    return this.request(`/elections/${id}/metadata`);
  }
  async getElectionReviews(id) {
    return this.request(`/elections/${id}/reviews`);
  }
  async setElectionEligibleVoters(id, memberIds) {
    const voterIds = Array.isArray(memberIds)
      ? [...new Set(memberIds.map(Number).filter(Number.isInteger))]
      : [];
    return this.request(`/elections/${id}/eligible-voters`, {
      method: "PUT",
      body: JSON.stringify({ member_ids: voterIds }),
    });
  }
  async submitElectionReview(id, data) {
    return this.request(`/elections/${id}/reviews`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async decideElectionReview(id, reviewId, data) {
    return this.request(`/elections/${id}/reviews/${reviewId}/decision`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async applyCandidate(electionId, data) {
    return this.request(`/elections/${electionId}/apply`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getCandidates(electionId) {
    return this.request(`/elections/${electionId}/candidates`);
  }

  async requestVoteOTP(electionId, memberId) {
    return this.request(`/elections/${electionId}/request-otp`, {
      method: "POST",
      body: JSON.stringify({ member_id: memberId }),
    });
  }

  async vote(electionId, candidateId, otp, choice = "candidate") {
    return this.request(`/elections/${electionId}/vote`, {
      method: "POST",
      body: JSON.stringify({ candidate_id: candidateId, otp, choice }),
    });
  }

  async getElectionResults(electionId) {
    return this.request(`/elections/${electionId}/results`);
  }
  async printElectionResults(electionId) {
    return this.openAuthenticatedHtml(`/elections/${electionId}/results/print`);
  }
  async downloadElectionResultsPdf(electionId) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/elections/${electionId}/results/pdf`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Could not generate the results PDF.");
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `TFM-Election-${electionId}-Results.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return { status: true, message: "Results PDF downloaded." };
  }
  async previewElectionResultsPdf(electionId) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/elections/${electionId}/results/pdf?preview=1`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Could not preview the results PDF.");
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const preview = window.open(url, "_blank");
    if (!preview) {
      URL.revokeObjectURL(url);
      throw new Error("Allow pop-ups to preview the PDF.");
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  async printElectionCertificate(electionId, candidateId) {
    return this.openAuthenticatedHtml(
      `/elections/${electionId}/certificates/${candidateId}`,
    );
  }
  async saveElectionHandover(electionId, data) {
    return this.request(`/elections/${electionId}/handover`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getVoteStatus(electionId) {
    return this.request(`/elections/${electionId}/vote-status`);
  }

  // Recruitment (Matangazo ya Ajira)
  async getPublicJobs() {
    return this.publicRequest("/careers/jobs");
  }

  async getPublicJob(id) {
    return this.publicRequest(`/careers/jobs/${id}`);
  }

  async submitPublicApplication(formData) {
    const response = await fetch(`${this.baseUrl}/careers/applications`, { method: "POST", body: formData });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.messages?.error || data.message || "Application could not be submitted.");
    return data;
  }

  async trackPublicApplication(application_number, email) {
    return this.publicRequest("/careers/applications/track", {
      method: "POST",
      body: JSON.stringify({ application_number, email }),
    });
  }

  async getJobs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(
      `/recruitment/jobs${queryString ? `?${queryString}` : ""}`,
    );
  }

  async getOpenJobs() {
    return this.request("/recruitment/jobs/open");
  }

  async getJob(id) {
    return this.request(`/recruitment/jobs/${id}`);
  }

  async createJob(data) {
    return this.request("/recruitment/jobs", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateJob(id, data) {
    return this.request(`/recruitment/jobs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteJob(id) {
    return this.request(`/recruitment/jobs/${id}`, {
      method: "DELETE",
    });
  }

  async getApplications(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(
      `/recruitment/applications${queryString ? `?${queryString}` : ""}`,
    );
  }

  async getApplication(id) {
    return this.request(`/recruitment/applications/${id}`);
  }

  async openApplicationDocument(id, key, name = "document") {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(`${this.baseUrl}/recruitment/applications/${id}/documents/${key}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) throw new Error("Document could not be opened.");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    if (/^(application\/pdf|image\/)/.test(blob.type)) a.target = "_blank";
    else a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  async submitApplication(formData) {
    const token = localStorage.getItem("tarewu_token");
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/recruitment/applications`, {
      method: "POST",
      headers,
      body: formData, // Send FormData directly (no Content-Type header)
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      data = text;
    }

    if (!response.ok) {
      const message =
        data && data.message
          ? data.message
          : typeof data === "string"
            ? data
            : JSON.stringify(data);
      throw new Error(message || `API request failed (${response.status})`);
    }

    return data;
  }

  async updateApplicationStatus(id, data) {
    return this.request(`/recruitment/applications/${id}/status`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async withdrawApplication(id) {
    return this.request(`/recruitment/applications/${id}/withdraw`, {
      method: "POST",
    });
  }

  async deleteApplication(id) {
    return this.request(`/recruitment/applications/${id}`, {
      method: "DELETE",
    });
  }

  async getSmsLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/sms-logs${queryString ? `?${queryString}` : ""}`);
  }

  async clearSmsLogs() {
    return this.request("/sms-logs", { method: "DELETE" });
  }

  async getSystemLogFiles() {
    return this.request("/system-logs");
  }

  async getSystemLog(name, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/system-logs/${encodeURIComponent(name)}${queryString ? `?${queryString}` : ""}`);
  }

  async deleteSystemLog(name) {
    return this.request(`/system-logs/${encodeURIComponent(name)}`, { method: "DELETE" });
  }

  async clearSystemLogs() {
    return this.request("/system-logs", { method: "DELETE" });
  }

  async getRecruitmentStatistics() {
    return this.request("/recruitment/statistics");
  }

  // ============ ID Cards ============
  async getIDCards(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/id-cards?${params.toString()}`);
  }

  async getIDCard(id) {
    return this.request(`/id-cards/${id}`);
  }

  async createIDCard(data) {
    return this.request("/id-cards", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateIDCard(id, data) {
    return this.request(`/id-cards/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteIDCard(id) {
    return this.request(`/id-cards/${id}`, {
      method: "DELETE",
    });
  }

  async getMemberIDCard(memberId) {
    return this.request(`/id-cards/member/${memberId}`);
  }

  async getMyIDCard() {
    return this.request("/id-cards/me");
  }

  async verifyDigitalIdentity(token) {
    return this.request(`/digital-identities/verify/${encodeURIComponent(token)}`);
  }

  async initiateIDCardPayment(cardId, data) {
    return this.request(`/id-cards/${cardId}/initiate-payment`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async confirmIDCardPayment(cardId, data) {
    return this.request(`/id-cards/${cardId}/confirm-payment`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async generateIDCardPDF(cardId) {
    const url = `${this.baseUrl}/id-cards/${cardId}/generate-pdf`;
    const token = localStorage.getItem("tarewu_token");

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to generate PDF");
    }

    // Get the blob data
    const blob = await response.blob();

    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `ID_Card_${cardId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);

    return { status: true, message: "PDF downloaded successfully" };
  }

  // ============ Card Templates ============
  async getCardTemplates(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/card-templates?${params.toString()}`);
  }

  async getCardTemplate(id) {
    return this.request(`/card-templates/${id}`);
  }

  async createCardTemplate(data) {
    return this.request("/card-templates", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCardTemplate(id, data) {
    return this.request(`/card-templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCardTemplate(id) {
    return this.request(`/card-templates/${id}`, {
      method: "DELETE",
    });
  }

  async setDefaultCardTemplate(id) {
    return this.request(`/card-templates/${id}/set-default`, {
      method: "POST",
    });
  }

  // FormData methods for file uploads
  async createCardTemplateWithFile(formData) {
    const url = `${this.baseUrl}/card-templates`;
    const token = localStorage.getItem("tarewu_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type, let browser set it with boundary for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create template");
    }

    return response.json();
  }

  async updateCardTemplateWithFile(id, formData) {
    const url = `${this.baseUrl}/card-templates/${id}`;
    const token = localStorage.getItem("tarewu_token");

    const response = await fetch(url, {
      method: "POST", // Use POST with _method=PUT for file uploads
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update template");
    }

    return response.json();
  }

  // Accounting
  async getFinancialYears() {
    return this.request("/accounting/years");
  }
  async createFinancialYear(data) {
    return this.request("/accounting/years", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionFinancialYear(id, action) {
    return this.request(`/accounting/years/${id}/${action}`, {
      method: "POST",
      body: "{}",
    });
  }
  async updateFinancialYear(id, data) {
    return this.request(`/accounting/years/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async getAccounts() {
    return this.request("/accounting/accounts");
  }
  async createAccount(data) {
    return this.request("/accounting/accounts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateAccount(id, data) {
    return this.request(`/accounting/accounts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async getCostCenters() {
    return this.request("/accounting/cost-centers");
  }
  async createCostCenter(data) {
    return this.request("/accounting/cost-centers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateCostCenter(id, data) {
    return this.request(`/accounting/cost-centers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async getApprovalWorkflows() {
    return this.request("/accounting/approval-workflows");
  }
  async createApprovalWorkflow(data) {
    return this.request("/accounting/approval-workflows", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateApprovalWorkflow(id, data) {
    return this.request(`/accounting/approval-workflows/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async setApprovalWorkflowStatus(id, status) {
    return this.request(`/accounting/approval-workflows/${id}/${status}`, {
      method: "POST",
      body: "{}",
    });
  }
  async getStaffRequisitions() {
    return this.request("/staff-requisitions");
  }
  async getStaffRequisitionMetadata() {
    return this.request("/staff-requisitions/metadata");
  }
  async createStaffRequisition(data) {
    return this.request("/staff-requisitions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateStaffRequisition(id, data) {
    return this.request(`/staff-requisitions/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async transitionStaffRequisition(id, action, reason = null) {
    return this.request(`/staff-requisitions/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async convertStaffRequisition(id, data) {
    return this.request(`/staff-requisitions/${id}/convert`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async getStaffRequisitionDocuments(id) {
    return this.request(`/staff-requisitions/${id}/documents`);
  }
  async staffRequisitionPdf(id, preview = true) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/staff-requisitions/${id}/pdf${preview ? "?preview=1" : ""}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.messages?.error || data.message || "Could not generate requisition PDF.");
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (preview) window.open(url, "_blank", "noopener,noreferrer");
    else {
      const link = document.createElement("a");link.href = url;link.download = `Staff-Requisition-${id}.pdf`;link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  async uploadStaffRequisitionDocument(id, file, title) {
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("document", file);
    form.append("title", title);
    const response = await fetch(
      `${this.baseUrl}/staff-requisitions/${id}/documents`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      },
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.messages?.error || data.message || "Document upload failed");
    return data;
  }
  async downloadStaffRequisitionDocument(id, documentId, title = "document") {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/staff-requisitions/${id}/documents/${documentId}/download`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Document download failed");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = title;
    link.click();
    URL.revokeObjectURL(url);
  }
  async getJournals(status = "") {
    return this.request(
      `/accounting/journals${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    );
  }
  async createJournal(data) {
    return this.request("/accounting/journals", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionJournal(id, action, reason = null) {
    return this.request(`/accounting/journals/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async reverseJournal(id, reason, transactionDate) {
    return this.request(`/accounting/journals/${id}/reverse`, {
      method: "POST",
      body: JSON.stringify({ reason, transaction_date: transactionDate }),
    });
  }
  async getTrialBalance() {
    return this.request("/accounting/trial-balance");
  }
  async getAccountingReport(report, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(
      `/accounting/reports/${report}${query ? `?${query}` : ""}`,
    );
  }
  async getReceipts() {
    return this.request("/accounting/receipts");
  }
  async createReceipt(data) {
    return this.request("/accounting/receipts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionReceipt(id, action, reason = null) {
    return this.request(`/accounting/receipts/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async printReceipt(id) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/receipts/${id}/print`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not generate receipt");
    const html = await response.text();
    const win = window.open("", "_blank");
    if (!win) throw new Error("Allow pop-ups to print the receipt");
    win.document.open();
    win.document.write(html);
    win.document.close();
  }
  async getPayments() {
    return this.request("/accounting/payments");
  }
  async createPayment(data) {
    return this.request("/accounting/payments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionPayment(id, action, reason = null) {
    return this.request(`/accounting/payments/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async getFinanceDocuments(type, id) {
    return this.request(`/accounting/documents/${type}/${id}`);
  }
  async uploadFinanceDocument(type, id, file, title) {
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("document", file);
    form.append("title", title);
    const response = await fetch(
      `${this.baseUrl}/accounting/documents/${type}/${id}`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      },
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Document upload failed");
    return data;
  }
  async downloadFinanceDocument(type, id, documentId, title = "document") {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/documents/${type}/${id}/${documentId}/download`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Document download failed");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = title;
    link.click();
    URL.revokeObjectURL(url);
  }
  async printPayment(id) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/payments/${id}/print`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not generate payment voucher");
    const html = await response.text();
    const win = window.open("", "_blank");
    if (!win) throw new Error("Allow pop-ups to print the voucher");
    win.document.open();
    win.document.write(html);
    win.document.close();
  }
  async getBankAccounts() {
    return this.request("/accounting/bank-accounts");
  }
  async createBankAccount(data) {
    return this.request("/accounting/bank-accounts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async addStatementTransactions(id, transactions) {
    return this.request(`/accounting/bank-accounts/${id}/statement`, {
      method: "POST",
      body: JSON.stringify({ transactions }),
    });
  }
  async getBankBook(id, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(
      `/accounting/bank-accounts/${id}/book${query ? `?${query}` : ""}`,
    );
  }
  async getBankTransfers() {
    return this.request("/accounting/bank-transfers");
  }
  async createBankTransfer(data) {
    return this.request("/accounting/bank-transfers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionBankTransfer(id, action, reason = null) {
    return this.request(`/accounting/bank-transfers/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async printBankTransfer(id) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/bank-transfers/${id}/print`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not generate transfer voucher");
    const html = await response.text();
    const win = window.open("", "_blank");
    if (!win) throw new Error("Allow pop-ups to print the voucher");
    win.document.open();
    win.document.write(html);
    win.document.close();
  }
  async downloadBankStatementSample(id) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/bank-accounts/${id}/statement-import-sample`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not download statement sample");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bank-statement-import-sample.csv";
    link.click();
    URL.revokeObjectURL(url);
  }
  async importBankStatement(id, file, commit = false) {
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("file", file);
    form.append("commit", commit ? "1" : "0");
    const response = await fetch(
      `${this.baseUrl}/accounting/bank-accounts/${id}/statement-import`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      },
    );
    const data = await response.json();
    if (!response.ok)
      throw new Error(data.message || "Statement import failed");
    return data;
  }
  async getReconciliations() {
    return this.request("/accounting/reconciliations");
  }
  async getReconciliation(id) {
    return this.request(`/accounting/reconciliations/${id}`);
  }
  async matchReconciliationItem(id, data) {
    return this.request(`/accounting/reconciliation-items/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async createReconciliation(data) {
    return this.request("/accounting/reconciliations", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionReconciliation(id, action) {
    return this.request(`/accounting/reconciliations/${id}/${action}`, {
      method: "POST",
      body: "{}",
    });
  }
  async getBankMatchingRules(){return this.request("/accounting/bank-matching-rules");}
  async createBankMatchingRule(data){return this.request("/accounting/bank-matching-rules",{method:"POST",body:JSON.stringify(data)});}
  async updateBankMatchingRule(id,data){return this.request(`/accounting/bank-matching-rules/${id}`,{method:"PUT",body:JSON.stringify(data)});}
  async acceptReconciliationSuggestions(id){return this.request(`/accounting/reconciliations/${id}/accept-suggestions`,{method:"POST",body:"{}"});}
  async autoMatchReconciliation(id){return this.request(`/accounting/reconciliations/${id}/auto-match`,{method:"POST",body:"{}"});}
  async getPurchaseOrders(){return this.request("/accounting/purchase-orders");}
  async createPurchaseOrder(data){return this.request("/accounting/purchase-orders",{method:"POST",body:JSON.stringify(data)});}
  async transitionPurchaseOrder(id,action,reason=null){return this.request(`/accounting/purchase-orders/${id}/${action}`,{method:"POST",body:JSON.stringify({reason})});}
  async receivePurchaseOrder(id,data){return this.request(`/accounting/purchase-orders/${id}/receive`,{method:"POST",body:JSON.stringify(data)});}
  async invoiceGoodsReceived(id,data){return this.request(`/accounting/grns/${id}/invoice`,{method:"POST",body:JSON.stringify(data)});}
  async getFixedAssets(){return this.request("/accounting/fixed-assets");}
  async createFixedAsset(data){return this.request("/accounting/fixed-assets",{method:"POST",body:JSON.stringify(data)});}
  async depreciateFixedAsset(id,through_date){return this.request(`/accounting/fixed-assets/${id}/depreciate`,{method:"POST",body:JSON.stringify({through_date})});}
  async getInvoices(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/accounting/invoices${query ? `?${query}` : ""}`);
  }
  async getInvoice(id) {
    return this.request(`/accounting/invoices/${id}`);
  }
  async printInvoice(id) {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/invoices/${id}/print`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not generate invoice");
    const html = await response.text();
    const win = window.open("", "_blank");
    if (!win) throw new Error("Allow pop-ups to print the invoice");
    win.document.write(html);
    win.document.close();
  }
  async createInvoice(data) {
    return this.request("/accounting/invoices", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async getAccountingPeriods(financialYearId=""){return this.request(`/accounting/periods${financialYearId?`?financial_year_id=${financialYearId}`:""}`);}
  async transitionAccountingPeriod(id,action){return this.request(`/accounting/periods/${id}/${action}`,{method:"POST"});}
  async saveBudgetPeriods(id,allocations){return this.request(`/accounting/budgets/${id}/periods`,{method:"PUT",body:JSON.stringify({allocations})});}
  async transitionInvoice(id, action, reason = null) {
    return this.request(`/accounting/invoices/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async cancelInvoice(id, reason) {
    return this.request(`/accounting/invoices/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async createCreditNote(invoiceId,data){return this.request(`/accounting/invoices/${invoiceId}/credit-notes`,{method:"POST",body:JSON.stringify(data)});}
  async transitionCreditNote(id,action,reason=null){return this.request(`/accounting/credit-notes/${id}/${action}`,{method:"POST",body:JSON.stringify({reason})});}
  async createInvoiceSettlement(id, data) {
    return this.request(`/accounting/invoices/${id}/settlements`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async transitionInvoiceSettlement(id, action, reason = null) {
    return this.request(`/accounting/settlements/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async getAccountingCustomers(search = "") {
    return this.request(
      `/accounting/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    );
  }
  async createAccountingCustomer(data) {
    return this.request("/accounting/customers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async syncAccountingCustomersFromMembers() {
    return this.request("/accounting/customers/sync-members", {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
  async getCustomerStatement(id) {
    return this.request(`/accounting/customers/${id}/statement`);
  }
  async getAccountingSuppliers(search = "") {
    return this.request(
      `/accounting/suppliers${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    );
  }
  async createAccountingSupplier(data) {
    return this.request("/accounting/suppliers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async getSupplierStatement(id) {
    return this.request(`/accounting/suppliers/${id}/statement`);
  }
  async getBudgets() {
    return this.request("/accounting/budgets");
  }
  async getBudget(id) {
    return this.request(`/accounting/budgets/${id}`);
  }
  async createBudget(data) {
    return this.request("/accounting/budgets", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateBudget(id, data) {
    return this.request(`/accounting/budgets/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async transitionBudget(id, action, reason = null) {
    return this.request(`/accounting/budgets/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }
  async downloadBudgetSample() {
    const token = localStorage.getItem("tarewu_token");
    const response = await fetch(
      `${this.baseUrl}/accounting/budgets/import-sample`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) throw new Error("Could not download budget sample");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "budget-import-sample.csv";
    link.click();
    URL.revokeObjectURL(url);
  }
  async importBudgets(file, commit = false) {
    const token = localStorage.getItem("tarewu_token");
    const form = new FormData();
    form.append("file", file);
    form.append("commit", commit ? "1" : "0");
    const response = await fetch(`${this.baseUrl}/accounting/budgets/import`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.message || "Budget import failed");
    return data;
  }
  async getSupportTickets(params = {}) {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value)).toString();
    return this.request(`/support/tickets${query ? `?${query}` : ""}`);
  }
  async createSupportTicket(data) { return this.request("/support/tickets", { method: "POST", body: JSON.stringify(data) }); }
  async getSupportTicket(id) { return this.request(`/support/tickets/${id}`); }
  async replySupportTicket(id, data) { return this.request(`/support/tickets/${id}/replies`, { method: "POST", body: JSON.stringify(data) }); }
  async updateSupportTicket(id, data) { return this.request(`/support/tickets/${id}`, { method: "PUT", body: JSON.stringify(data) }); }
  async getSupportReport() { return this.request("/support/report"); }
}

export const api = new ApiService();
export default api;
